// lib/seo.ts — shared SEO constants + structured-data (JSON-LD) builders (spec §4.5).
// One source of truth for the site URL, social image, and author identity so every
// page emits consistent Open Graph, Twitter, and schema.org metadata.

export const SITE_URL = 'https://upload.deb0.com';
export const OG_IMAGE = `${SITE_URL}/og-image.svg`;

/** The owner/author identity (fetched from deb0.com). */
export const AUTHOR = {
  name: 'Debajyoti Saikia',
  jobTitle: 'Principal Software Engineer',
  employer: 'Microsoft',
  url: 'https://deb0.com',
  sameAs: [
    'https://deb0.com',
    'https://github.com/DebajyotiSaikia',
    'https://www.linkedin.com/in/debajyotisaikia/',
    'https://blog.deb0.com',
  ],
} as const;

const author = {
  '@type': 'Person',
  name: AUTHOR.name,
  jobTitle: AUTHOR.jobTitle,
  worksFor: { '@type': 'Organization', name: AUTHOR.employer },
  url: AUTHOR.url,
  sameAs: [...AUTHOR.sameAs],
};

/** schema.org Person for the site author. */
export const personJsonLd: Record<string, unknown> = {
  '@context': 'https://schema.org',
  ...author,
};

/** schema.org WebSite for the site itself. */
export const websiteJsonLd: Record<string, unknown> = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'UPLOAD',
  url: SITE_URL,
  description: 'A work digital twin that captures how you think, execute, and what you know.',
  author,
};

/** schema.org SoftwareApplication for the product. */
export const softwareJsonLd: Record<string, unknown> = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'UPLOAD',
  applicationCategory: 'ProductivityApplication',
  operatingSystem: 'Web',
  description:
    'UPLOAD builds a work digital twin: it captures how you think, execute, and what you know, ' +
    'and exposes that distilled memory to your own AI agents over MCP.',
  url: SITE_URL,
  image: OG_IMAGE,
  author,
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
};

/** A schema.org FAQPage built from Q/A pairs. */
export function faqJsonLd(faqs: { q: string; a: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}
